import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';
import { observer } from 'mobx-react-lite';

import { cleanExpiredCashbacks, getRewardsLevelColor, markPopupSeen, shouldShowPopup } from '../../../../helpers';
import { CashbackClaimSuccessModal } from '../cashback/claim-success-modal';
import { CashbackLevel, ClaimCashbackPayload } from '../../../../models';
import { RewardsAnnouncementPopup } from './announcement-popup';
import { AspectRatioImage } from '../cashback/ratio-image';
import { RewardsDetailedPopup } from './detailed-popup';
import { EligibleGames } from './eligible-games';
import { MissionDetails } from './mission-details';
import { CashbackWinType } from '../../../../enums';
import { useStore } from '../../../../hooks';
import { RewardsWidget } from './widget';

import './style.scss';

interface RewardsProps {

}

export const Rewards: React.FC<RewardsProps> = observer(() => {

  const { gameStore, cashbackStore, rewardsNotificationStore, userActivityStore } = useStore();
  const { gameId, gameReady, currencySymbol, currencyCode } = gameStore;
  const { cashbackInfo: { data: cashbackInfo }, announcementSmallView, announcementModalOpen } = cashbackStore;

  const [claimLoading, setClaimLoading] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [claimLevel, setClaimLevel] = useState<CashbackLevel | null>(null);
  const [missionLevelId, setMissionLevelId] = useState<number | null>(null);
  const [eligibleGamesOpen, setEligibleGamesOpen] = useState(false);

  const cashbackId = cashbackInfo?.id;

  // Re-derived from the store each render so socket updates flow into an open mission popup
  const missionLevel = useMemo(() => {
    return cashbackInfo?.levels.find(({ id }) => id === missionLevelId) || null;
  }, [cashbackInfo, missionLevelId]);

  const missionLevelColor = useMemo(() => {
    if (!missionLevel || !cashbackInfo) return '';
    return getRewardsLevelColor(cashbackInfo.levels.indexOf(missionLevel), cashbackInfo.levels.length);
  }, [cashbackInfo, missionLevel]);

  useEffect(() => {
    getCashbackInfo();
  }, []);

  useEffect(() => {
    cleanExpiredCashbacks();
    if (cashbackId && shouldShowPopup(cashbackId)) {
      cashbackStore.setAnnouncementModalOpen(true);
    }
  }, [cashbackId]);

  const getCashbackInfo = useCallback(() => {
    cashbackStore.getCashbackInfo(gameId);
  }, [gameId]);

  const closeMissionPopups = useCallback(() => {
    setMissionLevelId(null);
    setEligibleGamesOpen(false);
  }, []);

  const handleInfoClick = useCallback((level: CashbackLevel) => {
    setMissionLevelId(level.id);
  }, []);

  // The Rewards instance survives the gameReady reset, so popup state must be cleared explicitly
  const handleGameRedirect = useCallback(() => {
    closeMissionPopups();
    setDetailsModalOpen(false);
  }, [closeMissionPopups]);

  const claimCashback = useCallback((level: CashbackLevel) => {
    if (claimLoading) return;
    const { id: levelId, winType } = level;
    setClaimLevel(level);
    setClaimLoading(true);
    const payload: ClaimCashbackPayload = { gameId, levelId };
    cashbackStore.claimCashback(payload)
      .then((data) => {
        if (data.errorMessage) {
          // claim error
          rewardsNotificationStore.enqueue(data.errorMessage);
        } else {
          const isWinTypeFreebet = winType === CashbackWinType.Freebet;
          // claim success
          getCashbackInfo();
          setDetailsModalOpen(false);
          closeMissionPopups();
          if (!isWinTypeFreebet) {
            setSuccessModalOpen(true);
          }
        }
      })
      .finally(() => {
        setClaimLoading(false);
      });
  }, [gameId, cashbackInfo, setClaimLoading, getCashbackInfo, claimLoading, closeMissionPopups]);

  const onClaimClick = useCallback(() => {
    const hasMultipleClaims = (cashbackInfo?.levels.filter(({ isCompleted, isClaimed }) => isCompleted && !isClaimed).length || 0) > 1;
    if (hasMultipleClaims) {
      setDetailsModalOpen(true);
    } else {
      const claimLevel = cashbackInfo?.levels.find(({ isCompleted, isClaimed }) => isCompleted && !isClaimed);
      if (claimLevel) {
        claimCashback(claimLevel);
      }
    }
  }, [claimCashback, cashbackInfo]);

  const handleWidgetClick = useCallback(() => {
    userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.RewardsWidget
    });
    setDetailsModalOpen(true);
  }, []);

  const handleAnnouncementClose = useCallback(() => {
    if (cashbackId) {
      markPopupSeen(cashbackId, false, cashbackInfo?.endDate);
    }
    cashbackStore.setAnnouncementModalOpen(false);
  }, [cashbackId, cashbackInfo]);

  if (!gameReady || !cashbackInfo) return null;

  return (
    <>
      <div className={`cashback-v2-wrapper ${detailsModalOpen ? 'open' : ''}`}>

        <AspectRatioImage />

        <RewardsWidget
          cashbackInfo={cashbackInfo}
          onClaimClick={onClaimClick}
          onClick={handleWidgetClick}
        />

        {
          detailsModalOpen && (
            <RewardsDetailedPopup
              open={detailsModalOpen}
              currency={currencyCode}
              cashbackInfo={cashbackInfo}
              onClaimClick={claimCashback}
              onInfoClick={handleInfoClick}
              onClose={() => {
                setDetailsModalOpen(false);
                closeMissionPopups();
              }}
            />
          )
        }

        {
          detailsModalOpen && missionLevel && !eligibleGamesOpen && (
            <MissionDetails
              level={missionLevel}
              color={missionLevelColor}
              currency={currencyCode}
              onClose={() => setMissionLevelId(null)}
              onSeeAll={() => setEligibleGamesOpen(true)}
              onGameRedirect={handleGameRedirect}
            />
          )
        }

        {
          detailsModalOpen && missionLevel && eligibleGamesOpen && (
            <EligibleGames
              games={missionLevel.games || []}
              onBack={() => setEligibleGamesOpen(false)}
              onClose={closeMissionPopups}
              onGameRedirect={handleGameRedirect}
            />
          )
        }

        {
          successModalOpen && (
            <CashbackClaimSuccessModal
              cashbackCurrencyCode={currencyCode}
              onClose={() => setSuccessModalOpen(false)}
              rewardAmount={claimLevel?.rewardAmount || 0}
            />
          )
        }

        {
          announcementModalOpen && (
            <RewardsAnnouncementPopup
              currency={currencyCode || currencySymbol}
              cashbackInfo={cashbackInfo}
              smallView={announcementSmallView}
              onClose={handleAnnouncementClose}
            />
          )
        }

      </div>
    </>
  );
});
